import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@services/api.service';
import { environment } from '@env/environment';
import { enumCompanyPaymentType } from '../enums/companies';

import {
  ManagementCompanyModel,
  ManagementCompanySearchModel,
  ManagementCompanySettingsModel,
  ManagementLdsConnectionModel,
  UpdateCompanySupportSettingsManagementModel,
} from '@data-layer/models/management';
import { PaginatedResultModel } from '@data-layer/models/common';
import { UpdateCompanyRequestModel } from '../models/companies';
import { DistributionGroupLogActualSearchPagination } from '@app/modules/distribution-group-log/models';
import { DistributionGroupLogModel } from '@data-layer/models/management/distribution';

@Injectable({
  providedIn: 'root',
})
export class CompaniesManagementRepository {
  constructor(private apiService: ApiService) {}

  public getCompany(id: number, isShowLoader: boolean = true): Observable<ManagementCompanyModel> {
    return this.apiService.get<ManagementCompanyModel>(`${environment.url.companiesManagement}${id}`, null, { isShowLoader });
  }

  public searchCompanies(
    params?: ManagementCompanySearchModel,
    isShowLoader: boolean = true,
  ): Observable<PaginatedResultModel<ManagementCompanyModel>> {
    return this.apiService.get<PaginatedResultModel<ManagementCompanyModel>>(
      `${environment.url.companiesManagement}`,
      {
        params,
      },
      { isShowLoader },
    );
  }

  public setLdsCConnection(id: number, ldsConnection: ManagementLdsConnectionModel): Observable<never> {
    return this.apiService.put(`${environment.url.companiesManagement}${id}/credentials`, ldsConnection);
  }

  public setPaymentType(
    companyId: number,
    data: { freightPaymentType: enumCompanyPaymentType; customsPaymentType: enumCompanyPaymentType },
  ): Observable<never> {
    return this.apiService.put(`${environment.url.companiesManagement}${companyId}/payment-type`, data);
  }

  public update(id: number, request: UpdateCompanyRequestModel): Observable<void> {
    return this.apiService.put(`${environment.url.companiesManagement}${id}`, request);
  }

  public searchCompaniesBrokerDistribution(
    params?: DistributionGroupLogActualSearchPagination,
    isShowLoader: boolean = true,
  ): Observable<PaginatedResultModel<DistributionGroupLogModel>> {
    return this.apiService.get<PaginatedResultModel<DistributionGroupLogModel>>(
      `${environment.url.companiesManagement}broker-distribution`,
      { params },
      {
        isShowLoader,
      },
    );
  }

  public updateInstantDuties(
    id: number,
    isInstantDutiesPayment: boolean | null,
    isShowLoader?: boolean,
  ): Observable<ManagementCompanyModel> {
    return this.apiService.put<ManagementCompanyModel>(
      `${environment.url.companiesManagement}${id}/is-instant-duties-payment`,
      isInstantDutiesPayment,
      null,
      {
        isShowLoader,
      },
    );
  }

  public updateDutiesConfirmation(
    id: number,
    isDutiesConfirmation: boolean | null,
    isShowLoader?: boolean,
  ): Observable<ManagementCompanyModel> {
    return this.apiService.put<ManagementCompanyModel>(
      `${environment.url.companiesManagement}${id}/is-duties-confirmation`,
      isDutiesConfirmation,
      null,
      {
        isShowLoader,
      },
    );
  }

  public updateCollectionFee(
    id: number,
    isInstantDutiesPayment: boolean | null,
    isShowLoader?: boolean,
  ): Observable<ManagementCompanyModel> {
    return this.apiService.put<ManagementCompanyModel>(
      `${environment.url.companiesManagement}${id}/is-collection-fee`,
      isInstantDutiesPayment,
      null,
      {
        isShowLoader,
      },
    );
  }

  public updateCompanySettings(id: number, companySettings: ManagementCompanySettingsModel, isShowLoader?: boolean): Observable<void> {
    return this.apiService.put<void>(`${environment.url.companiesManagement}${id}/settings`, companySettings, null, {
      isShowLoader,
    });
  }

  public deleteCompany(companyId: number): Observable<void> {
    return this.apiService.delete<void>(`${environment.url.companiesManagement}${companyId}`);
  }

  public setCompanySupportSettings(
    companyId: number,
    params: UpdateCompanySupportSettingsManagementModel,
    isShowLoader: boolean = false,
  ): Observable<void> {
    return this.apiService.put<void>(`${environment.url.companiesManagement}${companyId}/support-settings`, params, null, { isShowLoader });
  }
}
